import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [pages, setPages] = useState([])
  const location = useLocation()

  useEffect(() => {
    // Fetch pages from CMS or fallback to default navigation
    fetch('https://api.dev.auxo.dk/public/content/sider')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0))
          setPages(sorted)
        } else {
          setPages(defaultNav)
        }
      })
      .catch(() => setPages(defaultNav))
  }, [])

  const defaultNav = [
    { nav_label: 'Forside', path: '/' },
    { nav_label: 'Privat rengøring', path: '/privat-rengoering' },
    { nav_label: 'Erhvervsrengøring', path: '/erhvervsrengoering' },
    { nav_label: 'Kontakt', path: '/kontakt' },
  ]

  const navItems = pages.length > 0 ? pages : defaultNav

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false)
  }, [location.pathname])

  return (
    <header className="sticky top-0 z-40 bg-[#FAF9F5]/95 backdrop-blur-md border-b border-[#E2E7E2] transition-colors">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* ROW 1: Wordmark Logo & Contact CTAs (Desktop) / Burger & Logo (Mobile) */}
        <div className="h-16 min-[1200px]:h-22 flex items-center justify-between">
          
          {/* Wordmark Logo */}
          <Link 
            to="/" 
            className="group flex items-center gap-2.5 sm:gap-3 focus:outline-none"
            aria-label="Elses skrub og skur forside"
          >
            {/* Logo Monogram Emblem */}
            <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-[#285F41] text-[#FAF9F5] flex items-center justify-center relative shadow-xs border border-[#357753]/40 group-hover:bg-[#357753] group-hover:scale-105 transition-all duration-300 shrink-0">
              <span className="font-serif-display font-bold text-lg sm:text-2xl italic text-[#F2F7F4]">E</span>
              <i className="fa-solid fa-sparkles text-[7px] sm:text-[8px] text-[#A8C7B2] absolute top-1 sm:top-1.5 right-1 sm:right-1.5 opacity-80" aria-hidden="true" />
            </div>

            {/* Wordmark & Tagline */}
            <div className="flex flex-col">
              <span className="brand-wordmark text-lg sm:text-2xl font-bold text-[#285F41] tracking-tight group-hover:text-[#357753] transition-colors leading-tight">
                Elses <span className="font-normal italic text-[#357753]">skrub &amp; skur</span>
              </span>
              <span className="text-[9px] sm:text-[11px] font-semibold tracking-wider text-[#285F41] uppercase flex items-center gap-1 sm:gap-1.5 -mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[#285F41]"></span>
                <span>Rengøring i Skjern &amp; omegn</span>
              </span>
            </div>
          </Link>

          {/* Desktop Row 1 Right: Contact details & CTA */}
          <div className="hidden min-[1200px]:flex items-center gap-6">
            <a
              href="tel:52506794"
              className="contact-no-break text-sm font-semibold text-[#285F41] hover:text-[#357753] flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#EEF3EF] transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-[#EEF3EF] text-[#285F41] flex items-center justify-center shadow-2xs">
                <i className="fa-regular fa-phone text-sm" aria-hidden="true"></i>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] text-[#5C7063] uppercase tracking-wider font-bold">Ring direkte</span>
                <span className="text-sm font-bold text-[#285F41]">52 50 67 94</span>
              </div>
            </a>

            <a
              href="mailto:else_christiansen@godmail.dk"
              className="contact-no-break text-sm font-semibold text-[#285F41] hover:text-[#357753] flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-[#EEF3EF] transition-colors"
            >
              <div className="w-9 h-9 rounded-lg bg-[#EEF3EF] text-[#285F41] flex items-center justify-center shadow-2xs">
                <i className="fa-regular fa-envelope text-sm" aria-hidden="true"></i>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="text-[10px] text-[#5C7063] uppercase tracking-wider font-bold">Send e-mail</span>
                <span className="text-sm font-bold text-[#285F41]">else_christiansen@godmail.dk</span>
              </div>
            </a>

            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-[#285F41] text-white text-sm font-semibold hover:bg-[#357753] shadow-sm transition-all active:scale-[0.98] gap-2"
            >
              <i className="fa-regular fa-paper-plane text-xs" aria-hidden="true"></i>
              <span>Kontakt mig</span>
            </Link>
          </div>

          {/* Burger Button - Shown ONLY below 1200px */}
          <div className="min-[1200px]:hidden flex items-center">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="px-3 py-2 rounded-xl text-[#285F41] bg-[#EEF3EF] border border-[#D5E2D8] hover:bg-[#E2ECE4] focus:outline-none transition-colors flex items-center gap-2 font-semibold text-xs"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn navigationsmenu"
            >
              <span>Menu</span>
              <i className={`fa-regular ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-sm`} aria-hidden="true"></i>
            </button>
          </div>
        </div>

        {/* MOBILE ROW 2: Quick Action Bar (< 1200px) */}
        <div className="min-[1200px]:hidden border-t border-[#E2E7E2]/80 py-2 flex items-center justify-between gap-2">
          <a
            href="tel:52506794"
            className="contact-no-break flex-1 flex items-center justify-center gap-2 text-xs font-bold text-[#285F41] bg-[#EEF3EF] border border-[#D5E2D8] py-2 px-3 rounded-xl hover:bg-[#E2ECE4] transition-colors"
          >
            <i className="fa-regular fa-phone text-[#285F41]" aria-hidden="true"></i>
            <span>Ring: 52 50 67 94</span>
          </a>

          <Link
            to="/kontakt"
            className="flex-1 flex items-center justify-center gap-2 text-xs font-bold text-white bg-[#285F41] py-2 px-3 rounded-xl shadow-2xs hover:bg-[#357753] transition-colors"
          >
            <i className="fa-regular fa-paper-plane text-[11px]" aria-hidden="true"></i>
            <span>Kontakt mig</span>
          </Link>
        </div>

        {/* ROW 2: Desktop Navigation Bar (Shown >= 1200px) */}
        <div className="hidden min-[1200px]:flex items-center justify-between border-t border-[#E2E7E2]/80 py-2.5">
          <nav className="flex items-center gap-1.5">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`text-sm tracking-wide transition-all px-4 py-2 rounded-lg relative ${
                    isActive
                      ? 'bg-[#285F41] text-white font-bold shadow-xs'
                      : 'text-[#3E5244] font-semibold hover:text-[#285F41] hover:bg-[#EEF3EF]'
                  }`}
                >
                  {item.nav_label}
                </Link>
              )
            })}
          </nav>

          <div className="text-xs font-semibold text-[#4F6355] flex items-center gap-2 px-3.5 py-1.5 bg-[#EEF3EF] rounded-full border border-[#D5E2D8]">
            <i className="fa-solid fa-location-dot text-[#285F41]"></i>
            <span>Vardevej 58, 6900 Skjern</span>
          </div>
        </div>

      </div>

      {/* Mobile Menu Overlay & Panel - Shown below 1200px */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-b border-[#E2E7E2] bg-[#FAF9F5] px-4 pt-3 pb-6 shadow-lg animate-fadeIn">
          <div className="flex flex-col gap-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`px-4 py-3 rounded-lg text-base font-semibold transition-colors flex items-center justify-between ${
                    isActive
                      ? 'bg-[#285F41] text-white'
                      : 'text-[#285F41] hover:bg-[#EEF3EF]'
                  }`}
                >
                  <span>{item.nav_label}</span>
                  {isActive && <i className="fa-regular fa-check text-xs" aria-hidden="true"></i>}
                </Link>
              )
            })}
            <div className="pt-4 mt-2 border-t border-[#E2E7E2] flex flex-col gap-3">
              <a
                href="tel:52506794"
                className="contact-no-break flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-[#285F41] text-[#285F41] font-semibold text-sm hover:bg-[#EEF3EF] transition-colors"
              >
                <i className="fa-regular fa-phone" aria-hidden="true"></i>
                <span>Ring direkte: 52 50 67 94</span>
              </a>
              <Link
                to="/kontakt"
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#285F41] text-white font-semibold text-sm shadow-sm hover:bg-[#357753] transition-colors"
              >
                <i className="fa-regular fa-paper-plane" aria-hidden="true"></i>
                <span>Kontakt mig</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
